import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, lastValueFrom, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ConfigService } from './config.service';

export namespace MuxUsageService {
  export interface UsageQuota {
    name: string;          // "Session", "Weekly", "Sonnet"
    used: number;          // Display fraction, 0.0 - 1.0. For ChatGPT/Codex this matches "left" in /status.
    resets_at?: string;    // ISO datetime when quota resets
  }

  export interface UsageReport {
    provider: string;      // "claude", "gemini", etc.
    available: boolean;    // Whether usage data was fetched successfully
    error?: string;        // Error message if not available
    quotas: UsageQuota[];
    fetched_at: string;    // ISO datetime when data was fetched
    estimated_cost_30d?: number | null;  // Estimated API cost last 30 days (USD)
    estimated_cost_7d?: number | null;   // Estimated API cost last 7 days (USD)
  }
}

@Injectable({
  providedIn: 'root'
})
export class MuxUsageService {
  // Observable for usage report
  private usageSubject = new BehaviorSubject<MuxUsageService.UsageReport | null>(null);
  public usage$ = this.usageSubject.asObservable();

  // Loading state
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  // Minimum time between fetches per backend — prevents hammering the API
  static readonly MIN_FETCH_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
  private lastFetchedAt = new Map<string, number>(); // backendId → epoch ms
  private reportCache = new Map<string, MuxUsageService.UsageReport>(); // backendId → last known report

  // Polling interval — background refresh
  private pollingIntervalMs = 5 * 60 * 1000; // 5 minutes
  private pollingSubscription: any = null;

  constructor(private http: HttpClient) {
    // Expose for console debugging
    (<any>window).muxUsage = this;
  }

  /**
   * Normalize a report so quota.used always means "fraction consumed" (0=empty, 1=full).
   * ChatGPT/Codex backends report remaining rather than used — invert here once.
   */
  static normalizeReport(report: MuxUsageService.UsageReport): MuxUsageService.UsageReport {
    if (report.provider !== 'chatgpt') return report;
    return {
      ...report,
      quotas: report.quotas.map(q => ({ ...q, used: 1 - q.used })),
    };
  }

  private isFreshEnough(backendId: string): boolean {
    const last = this.lastFetchedAt.get(backendId);
    return !!last && (Date.now() - last) < MuxUsageService.MIN_FETCH_INTERVAL_MS;
  }

  async fetchUsage(backendId: string = 'claude-cli', agentId?: string): Promise<MuxUsageService.UsageReport | null> {
    const cacheKey = agentId ? `${backendId}:${agentId}` : backendId;
    if (this.isFreshEnough(cacheKey)) {
      const cached = this.reportCache.get(cacheKey) ?? this.usageSubject.getValue();
      console.log(`[USAGE] cache hit for ${cacheKey}`, cached?.quotas?.map(q => `${q.name}=${Math.round(q.used*100)}%`));
      if (cached) this.usageSubject.next(cached);
      return cached;
    }
    // No agent → no request. The agent-less path resolves to the flat default
    // credential on the server, which is never what the tab is showing
    // (jovai 2026-08-27: 148 stray requests read the wrong subscription).
    if (!agentId) {
      console.warn(`[USAGE] skipped fetch for ${backendId}: no agent id`);
      return null;
    }
    this.loadingSubject.next(true);

    try {
      const url = `${ConfigService.muxUrl}/backends/${backendId}/usage?agent_id=${encodeURIComponent(agentId)}`;
      const raw = await lastValueFrom(
        this.http.get<MuxUsageService.UsageReport>(url)
      );
      const report = MuxUsageService.normalizeReport(raw);
      console.log(`[USAGE] fetched for ${cacheKey}`, report.quotas?.map(q => `${q.name}=${Math.round(q.used*100)}%`));

      this.lastFetchedAt.set(cacheKey, Date.now());
      this.reportCache.set(cacheKey, report);
      this.usageSubject.next(report);
      return report;
    } catch (error) {
      console.error('Failed to fetch usage from lit-mux:', error);
      // Throttle retries after failure — don't hammer a rate-limited endpoint
      this.lastFetchedAt.set(cacheKey, Date.now());

      // Return an unavailable report on error
      const errorReport: MuxUsageService.UsageReport = {
        provider: backendId,
        available: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        quotas: [],
        fetched_at: new Date().toISOString()
      };
      this.usageSubject.next(errorReport);
      return errorReport;
    } finally {
      this.loadingSubject.next(false);
    }
  }

  /**
   * Start polling for usage updates
   */
  startPolling(backendId: string = 'claude-cli', intervalMs?: number, agentId?: string): void {
    this.stopPolling();

    if (intervalMs) {
      this.pollingIntervalMs = intervalMs;
    }

    if (!agentId) {
      console.warn(`[USAGE] polling not started for ${backendId}: no agent id`);
      return;
    }
    // Initial fetch
    this.fetchUsage(backendId, agentId);

    // Set up polling
    const agentParam = `?agent_id=${encodeURIComponent(agentId)}`;
    const cacheKey = agentId ? `${backendId}:${agentId}` : backendId;
    this.pollingSubscription = timer(this.pollingIntervalMs, this.pollingIntervalMs)
      .pipe(
        switchMap(() => this.http.get<MuxUsageService.UsageReport>(
          `${ConfigService.muxUrl}/backends/${backendId}/usage${agentParam}`
        )),
        catchError((error) => {
          console.error('Usage polling error:', error);
          return [];
        })
      )
      .subscribe((report) => {
        if (report) {
          const normalized = MuxUsageService.normalizeReport(report);
          this.reportCache.set(cacheKey, normalized);
          this.lastFetchedAt.set(cacheKey, Date.now());
          this.usageSubject.next(normalized);
        }
      });
  }

  /**
   * Stop polling for usage updates
   */
  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  /**
   * Invalidate the cache for a backend and fetch fresh data.
   * Call after a message send to ensure the bar reflects actual usage.
   */
  refreshAfterSend(backendId: string, agentId?: string): void {
    const cacheKey = agentId ? `${backendId}:${agentId}` : backendId;
    this.lastFetchedAt.delete(cacheKey);
    this.fetchUsage(backendId, agentId);
  }

  /**
   * Get the current usage report (synchronously) — the active polled report.
   */
  getCurrentUsage(): MuxUsageService.UsageReport | null {
    return this.usageSubject.getValue();
  }

  /**
   * Get the cached report for a backend (+ agent) synchronously — the same key
   * fetchUsage/startPolling/fetchForCache write, so a caller that reads it sees
   * every poll, not just the first fetch.
   */
  getCachedReport(backendId: string, agentId?: string): MuxUsageService.UsageReport | null {
    const cacheKey = agentId ? `${backendId}:${agentId}` : backendId;
    return this.reportCache.get(cacheKey) ?? null;
  }

  /**
   * Force refresh the OAuth token (for testing token refresh flow)
   * Call from console: muxUsage.forceRefreshToken()
   */
  async forceRefreshToken(backendId: string = 'claude-cli', agentId?: string): Promise<any> {
    console.log('🔄 Force refreshing OAuth token...');

    try {
      const agentParam = agentId ? `&agent_id=${encodeURIComponent(agentId)}` : '';
      const url = `${ConfigService.muxUrl}/backends/${backendId}/usage?forceRefresh=true${agentParam}`;
      const report = await lastValueFrom(
        this.http.get<MuxUsageService.UsageReport>(url)
      );

      console.log('✅ Token refresh complete. Usage report:', report);
      this.usageSubject.next(report);
      return report;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Ensure the user's OAuth token is valid, refreshing if needed.
   * Called on app init to prevent "token expired" errors for users
   * who open the app after their token expired while away.
   *
   * This silently calls the usage endpoint which triggers server-side
   * token refresh if the token is expired or expiring soon.
   */
  async ensureValidToken(backendId: string = 'claude-cli', agentId?: string): Promise<boolean> {
    try {
      const agentParam = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : '';
      const url = `${ConfigService.muxUrl}/backends/${backendId}/usage${agentParam}`;
      await lastValueFrom(this.http.get<MuxUsageService.UsageReport>(url));
      return true;
    } catch (error) {
      console.warn('Token validation failed, user may need to re-authenticate:', error);
      return false;
    }
  }

  /**
   * Helper to format a quota percentage for display
   */
  static formatPercentage(used: number): string {
    return `${Math.round(used * 100)}%`;
  }

  /**
   * Fetch usage for a backend without updating the shared usageSubject.
   * Use this for background/cache fetches that shouldn't change the active report.
   */
  async fetchForCache(backendId: string, agentId?: string): Promise<MuxUsageService.UsageReport | null> {
    const cacheKey = agentId ? `${backendId}:${agentId}` : backendId;
    if (this.isFreshEnough(cacheKey)) {
      return this.reportCache.get(cacheKey) ?? null;
    }
    try {
      const agentParam = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : '';
      const url = `${ConfigService.muxUrl}/backends/${backendId}/usage${agentParam}`;
      const raw = await lastValueFrom(this.http.get<MuxUsageService.UsageReport>(url));
      const report = MuxUsageService.normalizeReport(raw);
      this.lastFetchedAt.set(cacheKey, Date.now());
      this.reportCache.set(cacheKey, report);
      return report;
    } catch {
      return null;
    }
  }

  /**
   * Helper to format reset time as relative time
   */
  static formatResetTime(resetsAt: string): string {
    const resetDate = new Date(resetsAt);
    const now = new Date();
    const diffMs = resetDate.getTime() - now.getTime();

    if (diffMs <= 0) {
      return 'now';
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const remainingMinutes = diffMinutes % 60;

    if (diffHours >= 24) {
      const days = Math.floor(diffHours / 24);
      const remainingHours = diffHours % 24;
      return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${remainingMinutes}m`;
    } else {
      return `${diffMinutes}m`;
    }
  }

  /**
   * Get CSS class based on usage level. quota.used is always "fraction consumed" (0=empty, 1=full).
   */
  static getUsageClass(
    used: number,
    report?: MuxUsageService.UsageReport | null | undefined,
  ): string {
    if (used >= 0.9) {
      return 'usage-critical';
    } else if (used >= 0.7) {
      return 'usage-warning';
    } else {
      return 'usage-normal';
    }
  }
}
